/**
 * InteractiveMiniTool — renders a rule-based mini-tool from an AI-generated
 * mini_tool_config blueprint.
 *
 * Pure client-side runtime — NO API calls during user interaction. The config
 * was generated once by Claude during the content-package pipeline; from then
 * on, the verdict is computed locally using simple if/then rules.
 *
 * Shape contract (matches content_engine.generators.generate_mini_tool_config):
 *   {
 *     title, subtitle,
 *     inputs: [{key, label, type: 'boolean'|'select'|'number', options?, min?, max?, help?}],
 *     rules:  [{when: [{key, op, value}], score, reason}],
 *     thresholds_numeric: {moderate_min, high_min},
 *     verdict_copy: {low|moderate|high: {headline, guidance}},
 *     cta: {label, destination}
 *   }
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Wrench, ArrowRight } from "lucide-react";

function evalCondition(values, cond) {
    const lhs = values[cond.key];
    const rhs = cond.value;
    switch (cond.op) {
        case "eq":
            // eslint-disable-next-line eqeqeq
            return lhs == rhs;
        case "ne":
            // eslint-disable-next-line eqeqeq
            return lhs != rhs;
        case "gte":
            return Number(lhs) >= Number(rhs);
        case "lte":
            return Number(lhs) <= Number(rhs);
        case "gt":
            return Number(lhs) > Number(rhs);
        case "lt":
            return Number(lhs) < Number(rhs);
        case "in":
            return Array.isArray(rhs) ? rhs.includes(lhs) : false;
        default:
            return false;
    }
}

function classifyScore(score, thresholds) {
    const modMin = Number(thresholds?.moderate_min ?? 3);
    const highMin = Number(thresholds?.high_min ?? 6);
    if (score >= highMin) return "high";
    if (score >= modMin) return "moderate";
    return "low";
}

export default function InteractiveMiniTool({ config, articleSlug, onCompleted }) {
    const [values, setValues] = useState({});
    const [submitted, setSubmitted] = useState(false);

    const result = useMemo(() => {
        if (!submitted || !config?.rules) return null;
        let score = 0;
        const matchedReasons = [];
        for (const rule of config.rules) {
            const conds = rule.when || [];
            if (conds.length && conds.every((c) => evalCondition(values, c))) {
                score += Number(rule.score) || 0;
                if (rule.reason) matchedReasons.push(rule.reason);
            }
        }
        const verdict = classifyScore(score, config.thresholds_numeric);
        return {
            score,
            verdict,
            reasons: matchedReasons,
            copy: config.verdict_copy?.[verdict] || {
                headline: verdict.charAt(0).toUpperCase() + verdict.slice(1) + " concern",
                guidance: "",
            },
        };
    }, [submitted, values, config]);

    if (!config) return null;

    const submit = (e) => {
        e?.preventDefault?.();
        setSubmitted(true);
        if (typeof window !== "undefined" && window.posthog?.capture) {
            try {
                window.posthog.capture("mini_tool_completed", {
                    tool_title: config.title,
                    article_slug: articleSlug,
                    verdict: result?.verdict,
                    score: result?.score,
                });
            } catch {
                /* no-op */
            }
        }
        onCompleted?.(result);
    };

    const reset = () => {
        setSubmitted(false);
        setValues({});
    };

    const verdictTones = {
        low: "border-[var(--bl-green)] bg-[var(--bl-green-soft)]",
        moderate: "border-[var(--bl-amber)] bg-[var(--bl-amber-soft)]",
        high: "border-[var(--bl-rust)] bg-[var(--bl-rust-tint)]",
    };

    return (
        <div className="bl-card p-6 md:p-7" data-testid="mini-tool-widget">
            <div className="flex items-center gap-2 mb-2">
                <Wrench className="h-4 w-4 text-bl-primary" />
                <h3 className="font-display font-semibold text-xl text-bl">
                    {config.title || "Mini Concern Checker"}
                </h3>
            </div>
            {config.subtitle && (
                <p className="text-sm text-bl-muted mb-5">{config.subtitle}</p>
            )}

            {!submitted ? (
                <form onSubmit={submit} className="space-y-4">
                    {(config.inputs || []).map((input) => (
                        <div key={input.key}>
                            <label className="block text-sm text-bl mb-1">
                                {input.label}
                            </label>
                            {input.type === "boolean" && (
                                <div className="flex gap-3">
                                    {["Yes", "No", "Not sure"].map((opt) => (
                                        <button
                                            type="button"
                                            key={opt}
                                            onClick={() =>
                                                setValues((p) => ({
                                                    ...p,
                                                    [input.key]:
                                                        opt === "Yes"
                                                            ? true
                                                            : opt === "No"
                                                              ? false
                                                              : null,
                                                }))
                                            }
                                            className={
                                                values[input.key] === (opt === "Yes") ||
                                                (values[input.key] === false && opt === "No") ||
                                                (values[input.key] === null && opt === "Not sure")
                                                    ? "bl-btn-primary text-xs"
                                                    : "bl-btn-ghost text-xs"
                                            }
                                            data-testid={`mt-${input.key}-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                                        >
                                            {opt}
                                        </button>
                                    ))}
                                </div>
                            )}
                            {input.type === "select" && (
                                <select
                                    value={values[input.key] ?? ""}
                                    onChange={(e) =>
                                        setValues((p) => ({
                                            ...p,
                                            [input.key]: e.target.value,
                                        }))
                                    }
                                    className="bl-input w-full"
                                    data-testid={`mt-${input.key}`}
                                >
                                    <option value="">— Choose one —</option>
                                    {(input.options || []).map((opt) => (
                                        <option key={opt} value={opt}>
                                            {opt}
                                        </option>
                                    ))}
                                </select>
                            )}
                            {input.type === "number" && (
                                <input
                                    type="number"
                                    min={input.min}
                                    max={input.max}
                                    value={values[input.key] ?? ""}
                                    onChange={(e) =>
                                        setValues((p) => ({
                                            ...p,
                                            [input.key]: e.target.value
                                                ? Number(e.target.value)
                                                : undefined,
                                        }))
                                    }
                                    className="bl-input w-full"
                                    data-testid={`mt-${input.key}`}
                                />
                            )}
                            {input.help && (
                                <p className="text-xs text-bl-muted mt-1">{input.help}</p>
                            )}
                        </div>
                    ))}
                    <button
                        type="submit"
                        className="bl-btn-primary"
                        data-testid="mt-submit"
                    >
                        Get my verdict
                    </button>
                </form>
            ) : (
                <div
                    className={`rounded border-l-4 p-5 ${verdictTones[result?.verdict] || ""}`}
                    data-testid={`mt-verdict-${result?.verdict}`}
                >
                    <div className="text-[10px] uppercase tracking-[0.2em] text-bl-muted">
                        {result?.verdict} concern · score {result?.score}
                    </div>
                    <h4 className="font-display font-semibold text-xl text-bl mt-1">
                        {result?.copy?.headline}
                    </h4>
                    <p className="text-bl mt-2">{result?.copy?.guidance}</p>
                    {result?.reasons?.length > 0 && (
                        <ul className="mt-3 list-disc pl-5 text-sm text-bl-muted space-y-1">
                            {result.reasons.map((r, i) => (
                                <li key={i}>{r}</li>
                            ))}
                        </ul>
                    )}
                    <div className="mt-5 flex items-center gap-3 flex-wrap">
                        {config.cta && (
                            <Link
                                to={config.cta.destination || "/upload"}
                                className="bl-btn-primary gap-1.5"
                                data-testid="mt-cta-primary"
                            >
                                {config.cta.label || "Upload Your Inspection Report"}
                                <ArrowRight className="h-4 w-4" />
                            </Link>
                        )}
                        <button
                            type="button"
                            onClick={reset}
                            className="bl-btn-ghost text-xs"
                            data-testid="mt-reset"
                        >
                            Try different answers
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
